import React, { useRef, useState } from 'react';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';
import { validateFileSize, validateFileType, fileToBase64 } from '../../utils/fileUtils';
import './ImageUpload.css';

const ImageUpload = ({
  value,
  onChange,
  multiple = false,
  maxSize = 5, // MB
  accept = 'image/jpeg,image/png,image/gif,image/webp',
  maxFiles = 10,
  preview = true,
  label = 'Upload Image',
  error,
  disabled = false
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const processFiles = async (files) => {
    const fileList = Array.from(files);
    const validFiles = [];
    const errors = [];

    for (const file of fileList) {
      if (!validateFileType(file, accept.split(','))) {
        errors.push(`${file.name}: Invalid file type`);
        continue;
      }
      if (!validateFileSize(file, maxSize)) {
        errors.push(`${file.name}: File size exceeds ${maxSize}MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (errors.length > 0) {
      setUploadError(errors[0]);
      setTimeout(() => setUploadError(''), 3000);
    }

    if (validFiles.length === 0) return;

    if (multiple) {
      const currentFiles = value || [];
      const newFiles = validFiles.slice(0, maxFiles - currentFiles.length);
      const previews = await Promise.all(newFiles.map(f => fileToBase64(f)));
      const filesWithPreviews = newFiles.map((file, i) => ({
        file,
        preview: previews[i],
        id: Date.now() + i
      }));
      onChange([...currentFiles, ...filesWithPreviews]);
    } else {
      const preview = await fileToBase64(validFiles[0]);
      onChange({ file: validFiles[0], preview });
    }
  };

  const handleChange = (e) => {
    if (e.target.files?.length > 0) {
      processFiles(e.target.files);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files?.length > 0 && !disabled) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleRemove = (id) => {
    if (multiple) {
      onChange(value.filter(f => f.id !== id));
    } else {
      onChange(null);
    }
  };

  const renderPreview = () => {
    if (!preview || !value) return null;

    if (multiple && Array.isArray(value) && value.length > 0) {
      return (
        <div className="upload-previews">
          {value.map((item) => (
            <div key={item.id} className="preview-item">
              <img src={item.preview} alt="Preview" />
              <button
                type="button"
                className="preview-remove"
                onClick={() => handleRemove(item.id)}
              >
                <FiX />
              </button>
            </div>
          ))}
        </div>
      );
    }

    if (!multiple && value?.preview) {
      return (
        <div className="upload-single-preview">
          <img src={value.preview} alt="Preview" />
          <button
            type="button"
            className="preview-remove"
            onClick={() => handleRemove()}
          >
            <FiX />
          </button>
        </div>
      );
    }

    // Handle existing image URL
    if (typeof value === 'string') {
      return (
        <div className="upload-single-preview">
          <img src={value} alt="Preview" />
          <button
            type="button"
            className="preview-remove"
            onClick={() => onChange(null)}
          >
            <FiX />
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`image-upload ${disabled ? 'disabled' : ''}`}>
      {label && <label className="upload-label">{label}</label>}
      
      <div
        className={`upload-zone ${dragActive ? 'drag-active' : ''} ${error || uploadError ? 'has-error' : ''}`}
        onClick={handleClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          disabled={disabled}
        />
        <div className="upload-content">
          <div className="upload-icon">
            <FiImage />
          </div>
          <div className="upload-text">
            <span className="upload-main-text">
              <FiUpload /> Click to upload or drag and drop
            </span>
            <span className="upload-sub-text">
              {accept.replace(/image\//g, '').replace(/,/g, ', ').toUpperCase()} (max {maxSize}MB)
            </span>
          </div>
        </div>
      </div>

      {renderPreview()}

      {(error || uploadError) && (
        <span className="upload-error">{error || uploadError}</span>
      )}
    </div>
  );
};

export default ImageUpload;
